// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { useEffect, useRef, useState, type ChangeEvent, type ReactElement } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import type {
    InfiniteScrollListRemoteFieldPredicateDescriptor,
    InfiniteScrollListRemoteFieldPredicateOption,
} from '@/components/ui/infinite-scroll-list.types';
import { useScheduledCursorSearch } from '@/hooks/useScheduledCursorSearch';

export interface RemotePredicateMultiselectProps {
    readonly descriptor: InfiniteScrollListRemoteFieldPredicateDescriptor;
    readonly selectedValues: ReadonlyArray<string>;
    readonly disabled?: boolean;
    readonly disabledReason?: string;
    readonly onSelectionChange: (
        option: InfiniteScrollListRemoteFieldPredicateOption,
        selected: boolean
    ) => void;
}

export function RemotePredicateMultiselect({
    descriptor,
    selectedValues,
    disabled = false,
    disabledReason,
    onSelectionChange,
}: RemotePredicateMultiselectProps): ReactElement {
    const detailsRef = useRef<HTMLDetailsElement | null>(null);
    const menuViewportRef = useRef<HTMLDivElement | null>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const search = useScheduledCursorSearch<InfiniteScrollListRemoteFieldPredicateOption, string>({
        minQueryLength: 0,
        delayMs: 300,
        pageSize: descriptor.remoteOptions.pageSize,
        fetchPage: descriptor.remoteOptions.fetchPage,
        getItemFilterText: (option) => `${option.label} ${option.secondaryLabel ?? ''}`,
        fetchOnEmptyQuery: true,
        enabled: isMenuOpen && !disabled,
        resetToken: descriptor.field,
    });

    useEffect(() => {
        if (
            !isMenuOpen ||
            search.isLoading ||
            !search.canLoadMore ||
            menuViewportRef.current === null ||
            loadMoreSentinelRef.current === null
        ) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries: ReadonlyArray<IntersectionObserverEntry>) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    search.loadMore();
                }
            },
            {
                root: menuViewportRef.current,
                rootMargin: '80px',
            }
        );
        observer.observe(loadMoreSentinelRef.current);
        return () => observer.disconnect();
    }, [isMenuOpen, search.canLoadMore, search.isLoading, search.loadMore]);

    return (
        <details
            ref={detailsRef}
            className="relative"
            title={disabled ? disabledReason : undefined}
            onToggle={(event) => {
                if (disabled && event.currentTarget.open) {
                    event.currentTarget.open = false;
                    setIsMenuOpen(false);
                    return;
                }
                setIsMenuOpen(event.currentTarget.open);
            }}
        >
            <summary
                aria-label={`${descriptor.label} values`}
                aria-disabled={disabled}
                className="flex h-9 min-w-[220px] cursor-pointer list-none items-center rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                onClick={(event) => {
                    if (disabled) {
                        event.preventDefault();
                    }
                }}
            >
                Select {descriptor.label}
            </summary>
            <div className="absolute right-0 z-20 mt-1 w-[360px] rounded-md border bg-background p-2 shadow-md">
                <Input
                    aria-label={`Search ${descriptor.label}`}
                    className="mb-2 h-9"
                    placeholder={`Search ${descriptor.label.toLowerCase()}...`}
                    value={search.queryText}
                    disabled={disabled}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        search.setQueryText(event.target.value)
                    }
                    onKeyDown={(event) => {
                        if (event.key === 'Escape' && detailsRef.current !== null) {
                            detailsRef.current.open = false;
                            setIsMenuOpen(false);
                        }
                    }}
                />
                {search.errorMessage !== null ? (
                    <Alert variant="destructive">
                        <AlertDescription>
                            Unable to load options. Close and reopen this menu to retry.
                        </AlertDescription>
                    </Alert>
                ) : null}
                <div
                    ref={menuViewportRef}
                    role="group"
                    aria-label={`${descriptor.label} options`}
                    className="max-h-72 overflow-y-auto"
                >
                    {search.visibleItems.map((option) => (
                        <label
                            key={option.value}
                            className="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                        >
                            <input
                                type="checkbox"
                                aria-label={`${descriptor.label}: ${option.label}`}
                                checked={selectedValues.includes(option.value)}
                                disabled={disabled}
                                onChange={(event) =>
                                    onSelectionChange(option, event.target.checked)
                                }
                            />
                            <span className="min-w-0">
                                <span className="block truncate">{option.label}</span>
                                {option.secondaryLabel !== undefined ? (
                                    <span className="block text-xs text-muted-foreground">
                                        {option.secondaryLabel}
                                    </span>
                                ) : null}
                            </span>
                        </label>
                    ))}
                    {search.visibleItems.length === 0 && !search.isLoading ? (
                        <div className="p-3 text-sm text-muted-foreground">No matches.</div>
                    ) : null}
                    <div
                        ref={loadMoreSentinelRef}
                        data-testid={`${descriptor.field}-option-load-sentinel`}
                        className="h-1"
                    />
                    {search.isLoading ? (
                        <div className="p-3 text-sm text-muted-foreground">Loading...</div>
                    ) : null}
                </div>
            </div>
        </details>
    );
}
